import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileSignature, Library } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { formatDate, formatInt, registerAuditLabels, lazyPage } from "@vixio/ui";
import "./api";
import type { ContractRow } from "./api";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const ContractsPage = lazyPage(() => import("./ContractsPage"), "ContractsPage");
const ContractPage = lazyPage(() => import("./ContractPage"), "ContractPage");
const LibraryPage = lazyPage(() => import("./LibraryPage"), "LibraryPage");

registerAuditLabels({
  "contracts.contract_created": "Contrato criado",
  "contracts.draft_updated": "Rascunho do contrato alterado",
  "contracts.sent_for_signature": "Contrato enviado para assinatura",
  "contracts.signature_canceled": "Envio para assinatura cancelado",
  "contracts.signature_expired": "Prazo de assinatura venceu",
  "contracts.contract_signed": "Contrato assinado",
  "contracts.amendment_created": "Aditivo criado",
  "contracts.amendment_signed": "Aditivo assinado",
  "contracts.amendment_canceled": "Aditivo cancelado",
  "contracts.contract_renewed": "Contrato renovado",
  "contracts.contract_expired": "Contrato vencido",
  "contracts.contract_terminated": "Contrato rescindido",
  "contracts.contract_canceled": "Contrato cancelado",
  "contracts.clause_created": "Cláusula criada",
  "contracts.clause_versioned": "Nova versão de cláusula",
  "contracts.template_created": "Modelo de contrato criado",
  "contracts.template_versioned": "Nova versão de modelo",
  "contracts.settings_set": "Parâmetros de contratos alterados",
});

function ContractsWidget() {
  const org = useOrg();
  const q = useQuery({
    queryKey: ["contracts", "widget", org.id],
    queryFn: () =>
      unwrap<Array<Pick<ContractRow, "id" | "number" | "counterparty_name" | "status" | "end_date" | "renewal_window">>>(
        from("contracts", "v_contracts")
          .select("id, number, counterparty_name, status, end_date, renewal_window")
          .eq("org_id", org.id)
          .or("status.eq.awaiting_signature,renewal_window.eq.true")
          .order("end_date", { ascending: true, nullsFirst: false })
          .limit(6),
      ),
  });
  const rows = q.data ?? [];
  return (
    <div className="vx-stack">
      <dl className="vx-dl">
        <dt>Aguardando assinatura</dt>
        <dd className="num">{formatInt(rows.filter((r) => r.status === "awaiting_signature").length)}</dd>
        <dt>Perto do fim</dt>
        <dd className={rows.some((r) => r.renewal_window) ? "num text-warn" : "num"}>{formatInt(rows.filter((r) => r.renewal_window).length)}</dd>
      </dl>
      {rows.filter((r) => r.renewal_window).length > 0 ? (
        <ul className="vx-stack" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {rows
            .filter((r) => r.renewal_window)
            .map((r) => (
              <li key={r.id} className="vx-row vx-row--between">
                <Link to={`/comercial/contratos/${r.id}`}>{r.counterparty_name}</Link>
                <span className="text-2 num">{formatDate(r.end_date)}</span>
              </li>
            ))}
        </ul>
      ) : null}
      <Link to="/comercial/contratos">Abrir contratos</Link>
    </div>
  );
}

export default defineModuleUi({
  key: "contracts",
  title: "Contratos",
  domain: "commercial",
  icon: FileSignature,
  menu: [
    { path: "/comercial/contratos", label: "Contratos", minRole: "member", icon: FileSignature, order: 35, keywords: "assinatura aditivo vigência renovação rescisão extenso" },
    { path: "/comercial/clausulas", label: "Cláusulas e modelos", minRole: "member", icon: Library, order: 36, keywords: "biblioteca modelo cláusula versão propriedade intelectual ia" },
  ],
  routes: [
    { path: "/comercial/contratos", element: <ContractsPage />, minRole: "member" },
    { path: "/comercial/contratos/:id", element: <ContractPage />, minRole: "member" },
    { path: "/comercial/clausulas", element: <LibraryPage />, minRole: "member" },
  ],
  widgets: [{ key: "contracts.attention", title: "Contratos", minRole: "member", span: 1, order: 32, element: ContractsWidget }],
});
